import { ObstructionsActionTypes } from 'actionsConstants';
import { createReducer } from '__common/utils/helpers';

const DEFAULT_OBSTRUCTION_NAME = /^Obstruction\s+\d+$/i;

function renumberDefaultObstructionNames(
  roofObstructions: { [obstructionId: number]: obstruction },
): { [obstructionId: number]: obstruction } {
  const ids = Object.keys(roofObstructions)
    .map(Number)
    .sort((a, b) => a - b);

  let nextNumber = 1;

  return ids.reduce((acc, id) => {
    const current = roofObstructions[id];
    const isDefaultName = DEFAULT_OBSTRUCTION_NAME.test(current.name || '');

    acc[id] = isDefaultName
      ? { ...current, name: `Obstruction ${nextNumber++}` }
      : current;

    return acc;
  }, {} as { [obstructionId: number]: obstruction });
}

export const ObstructionsState: obstructionsState = {
  obstructions: {},
  editingObstructionId: null,
};

export default {
  obstructions: createReducer(ObstructionsState, {
    [ObstructionsActionTypes.ADD_OBSTRUCTION](state, action: { payload: { roofId: number, obstructionId: number, name: string, length: number, width: number, coords: { x: number, y: number }[], isLargeEnough: boolean, metersPerPixel : number} }) {
      const { roofId, coords, obstructionId, name, length, width, isLargeEnough, metersPerPixel } = action.payload;

      const roofObstructions = state.obstructions[roofId] || {};

      return {
        ...state,
        obstructions: {
          ...state.obstructions,
          [roofId]: {
            ...roofObstructions,
            [obstructionId]: {
              coords,
              name,
              height: 0,
              setback: 0,
              length,
              width,
              isLargeEnough,
              metersPerPixel,
            },
          },
        },
      };
    },
    [ObstructionsActionTypes.ADD_OBSTRUCTIONS](state, action: { payload: { roofId: number, obstructions: roofObstructions } }) {
      const { roofId, obstructions } = action.payload;

      return {
        ...state,
        obstructions: {
          ...state.obstructions,
          [roofId]: obstructions,
        },
      };
    },
    [ObstructionsActionTypes.REMOVE_OBSTRUCTION](state, action: { payload: { roofId: number, obstructionId: number } }) {
      const { obstructions } = state;
      const { payload: { roofId, obstructionId } } = action;

      if (!obstructions[roofId] || !obstructions[roofId][obstructionId]) {
        return state;
      }

      const remainingRoofObstructions = { ...obstructions[roofId] };
      delete remainingRoofObstructions[obstructionId];

      // If no more obstructions on this roof, remove the roof entry
      const updatedObstructions = Object.keys(remainingRoofObstructions).length === 0
        ? (() => {
            const filtered = { ...obstructions };
            delete filtered[roofId];
            return filtered;
          })()
        : { ...obstructions, [roofId]: renumberDefaultObstructionNames(remainingRoofObstructions) };

      return {
        ...state,
        obstructions: updatedObstructions,
      };
    },
    [ObstructionsActionTypes.REMOVE_ROOF_OBSTRUCTIONS](state, action: { payload: { roofId: number } }) {
      const { obstructions } = state;
      const { payload: { roofId } } = action;

      if (!obstructions[roofId]) {
        return state;
      }

      const updatedObstructions = { ...obstructions };
      delete updatedObstructions[roofId];

      return {
        ...state,
        obstructions: updatedObstructions,
      };
    },
    [ObstructionsActionTypes.CHANGE_OBSTRUCTION_NAME](state, action: { payload: { roofId: number, obstructionId: number, name: string } }) {
      const { payload: { roofId, obstructionId, name } } = action;

      if (state.obstructions[roofId] && state.obstructions[roofId][obstructionId]) {
        // Create new object references to ensure Redux detects the change
        return {
          ...state,
          obstructions: {
            ...state.obstructions,
            [roofId]: {
              ...state.obstructions[roofId],
              [obstructionId]: {
                ...state.obstructions[roofId][obstructionId],
                name,
              },
            },
          },
        };
      }

      return state;
    },
    [ObstructionsActionTypes.CHANGE_OBSTRUCTION_HEIGHT](state, action: { payload: { roofId: number, obstructionId: number, height: string } }) {
      const { payload: { roofId, obstructionId, height } } = action;

      if (!state.obstructions[roofId] || !state.obstructions[roofId][obstructionId]) {
        return state;
      }

      let newHeight = Number(height); 

      if (isNaN(newHeight)) {
        newHeight = 0;
      }

      let newSetback = newHeight;
      const { width, length } = state.obstructions[roofId][obstructionId];

      if (length && width) {
        newSetback = Math.min(0.5 * Math.sqrt(Math.pow(length, 2) + Math.pow(width, 2)), Number(height));
      } 
      
      // Create a new obstruction object with updated height and setback
      // This ensures Redux detects the change properly
      return {
        ...state,
        obstructions: {
          ...state.obstructions,
          [roofId]: {
            ...state.obstructions[roofId],
            [obstructionId]: {
              ...state.obstructions[roofId][obstructionId],
              height: newHeight,
              setback: newSetback,
            },
          },
        },
      };
    },
    [ObstructionsActionTypes.SET_EDITING_OBSTRUCTION_ID](state, action: { payload: { obstructionId: number | null } }) {
      const { obstructionId } = action.payload;
      return { ...state, editingObstructionId: obstructionId };
    },
    [ObstructionsActionTypes.CLEAR_OBSTRUCTIONS]() {
      return { obstructions: {}, editingObstructionId: null };
    },
    [ObstructionsActionTypes.CENTER_OBSTRUCTIONS_ON_ROOF](state: obstructionsState, action: { payload: { xOffset: number, yOffset: number, roofId: number } }) {
      const { xOffset, yOffset, roofId } = action.payload;
      
      const obstructions = state.obstructions;

      if (!obstructions || !Object.keys(obstructions).length) {
        return state;
      }
      
      const obstructionsOnRoof = obstructions[roofId];

      if (!obstructionsOnRoof || !Object.keys(obstructionsOnRoof).length) {
        return state;
      }

      const centeredObstructionsOnRoof = Object.keys(obstructionsOnRoof).reduce((acc, obstructionId) => {
        const obstruction: obstruction = obstructionsOnRoof[obstructionId];

        const centeredCords = obstruction.coords.map(coord => {
          return {
            x: coord.x + xOffset,
            y: coord.y + yOffset,
          };
        });

        acc[obstructionId] = {
          ...obstruction,
          coords: centeredCords,
        };
        return acc;
      }, {});

      return {
        ...state,
        obstructions: {
          ...obstructions,
          [roofId]: centeredObstructionsOnRoof,
        },
      };
    },
  }),
};
